/**
 * Elite Batch 2 Part 4 — Workflow Templates 131–147
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}

// =============================================================================
// ELITE BATCH 2 PART 4 — TEMPLATES 131–147
// =============================================================================

export const ELITE_BATCH2_PART4: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 131. HEALTH/WELLNESS PROGRAM SIGNUP NO INTAKE (7 nodes)
  // ===================================================================

  "Health Wellness Program Signup No Intake": {
    nodes: [
      trigger("Webhook: Wellness program signup", "webhook_app", {
        source: "Health Platform / Custom Form",
        event: "wellness.signup",
        howItStarts:
          "A user signs up for a health/wellness program without completing the intake form",
        setup:
          "Connect your wellness platform webhook or form builder to fire when a signup occurs but no intake questionnaire is submitted. POST the raw payload to your workflow URL.",
        inputFields: [],
      }),

      // 2. AI extract signup details
      aiNode(
        "2",
        "AI extract signup details",
        280,
        "Read this wellness program signup payload. Extract the member name, email, phone, program name, signup date, and whether an intake form was completed. Respond JSON: {member_name: string, member_email: string, member_phone: string, program_name: string, signup_date: string, intake_completed: string}",
        "{webhook_payload}",
        [
          { field: "member_name", as: "member_name", type: "string" },
          { field: "member_email", as: "member_email", type: "string" },
          { field: "member_phone", as: "member_phone", type: "string" },
          { field: "program_name", as: "program_name", type: "string" },
          { field: "signup_date", as: "signup_date", type: "string" },
          { field: "intake_completed", as: "intake_completed", type: "string" },
        ],
      ),

      // 3. Check if intake is missing
      n("3", "condition", "Intake form completed?", 520, {
        conditionData: [
          {
            variable: "intake_completed",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. Send intake reminder email (False path — intake NOT completed)
      emailNode(
        "4",
        "Send intake form reminder email",
        760,
        "{member_email}",
        "Complete your intake form for {program_name}",
        "<p>Hi {member_name},</p><p>Welcome to <strong>{program_name}</strong>! We noticed you haven't completed your intake questionnaire yet.</p><p>Your intake form helps us personalize your wellness journey. Please complete it within 48 hours so your coach can prepare your plan.</p><p><a href='https://app.example.com/intake'>Complete Your Intake Form</a></p>",
      ),

      // 5. Delay 24 hours
      n("5", "delayAction", "Wait 24 hours", 1000, {
        delayAction: { time: 24, timeFormat: "hours" },
      }),

      // 6. Send SMS follow-up
      n("6", "sendSMS", "SMS: Intake form reminder", 1240, {
        sendSMSDetails: {
          to: "{member_phone}",
          smsBody:
            "Hi {member_name}, don't forget to complete your intake form for {program_name}. Your wellness coach is ready to help! Complete it here: https://app.example.com/intake",
        },
      }),

      // 7. Notify wellness coordinator via Slack
      slackNode(
        "7",
        "Notify coordinator: no intake",
        1480,
        "Wellness Alert: {member_name} ({member_email}) signed up for {program_name} on {signup_date} but has NOT completed the intake form after 24h. Manual follow-up may be needed.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_2"), // False: intake NOT completed
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
    summary: `**Health/Wellness Program Signup — No Intake** — Catches members who sign up for a wellness program but skip the intake questionnaire.\n\n1. Webhook fires when a wellness program signup is detected\n2. **AI extracts** member details and intake completion status\n3. **Condition** checks if the intake form was completed\n4. If NOT completed, sends a **reminder email** with a direct link to the intake form\n5. **Waits 24 hours** for the member to act\n6. **SMS follow-up** if still incomplete\n7. **Slack alert** to the wellness coordinator for manual outreach\n\n**To activate:** Say "connect openai", "connect slack", "connect twilio"`,
  },

  // ===================================================================
  // 132. HIGH-LTV CUSTOMER LOYALTY APP PROMPT (7 nodes)
  // ===================================================================

  "High LTV Customer Loyalty App Prompt": {
    nodes: [
      trigger("Webhook: High-LTV customer identified", "webhook_app", {
        source: "Stripe / CRM",
        event: "customer.ltv_threshold",
        howItStarts:
          "A customer crosses a high lifetime value threshold, triggering a loyalty app invitation",
        setup:
          "Set up a webhook in your CRM or billing system (Stripe) to fire when a customer's LTV exceeds your threshold. POST the customer payload to your workflow URL.",
        inputFields: [],
      }),

      // 2. AI extract customer data
      aiNode(
        "2",
        "AI extract customer LTV data",
        280,
        "Read this customer webhook payload. Extract customer name, email, lifetime value amount, total orders, account age in months, and their current plan or tier. Respond JSON: {customer_name: string, customer_email: string, ltv_amount: string, total_orders: string, account_age_months: string, current_plan: string}",
        "{webhook_payload}",
        [
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "ltv_amount", as: "ltv_amount", type: "string" },
          { field: "total_orders", as: "total_orders", type: "string" },
          {
            field: "account_age_months",
            as: "account_age_months",
            type: "string",
          },
          { field: "current_plan", as: "current_plan", type: "string" },
        ],
      ),

      // 3. Compute loyalty tier via code
      n("3", "runCode", "Compute loyalty tier", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `const ltv = parseFloat(inputs.ltv_amount) || 0;
let loyalty_tier = "silver";
let reward_percent = "5";
if (ltv >= 10000) { loyalty_tier = "platinum"; reward_percent = "15"; }
else if (ltv >= 5000) { loyalty_tier = "gold"; reward_percent = "10"; }
return { loyalty_tier, reward_percent, ltv_formatted: "$" + ltv.toFixed(2) };`,
        },
      }),

      // 4. Send loyalty app invitation email
      emailNode(
        "4",
        "Send loyalty app invitation",
        760,
        "{customer_email}",
        "You've earned {loyalty_tier} status — unlock exclusive rewards!",
        "<p>Hi {customer_name},</p><p>Thank you for being an incredible customer! With a lifetime value of <strong>{ltv_formatted}</strong> across <strong>{total_orders}</strong> orders, you've earned <strong>{loyalty_tier}</strong> status.</p><p>As a {loyalty_tier} member, you'll receive:</p><ul><li>{reward_percent}% discount on all future orders</li><li>Early access to new products</li><li>Priority customer support</li></ul><p><a href='https://app.example.com/loyalty/enroll'>Download the Loyalty App</a></p>",
      ),

      // 5. Push notification
      n("5", "pushNotification", "Push: Loyalty app download", 1000, {
        pushNotification: {
          title: "You're a {loyalty_tier} member!",
          body: "Hi {customer_name}, download our loyalty app to unlock {reward_percent}% rewards and exclusive perks.",
        },
      }),

      // 6. Log to CRM via API
      n("6", "hitApi", "Update CRM: loyalty tier", 1240, {
        hitApiDetails: {
          method: "PATCH",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/{customer_email}",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "properties",
              value:
                '{"loyalty_tier":"{loyalty_tier}","ltv_amount":"{ltv_amount}","loyalty_app_invited":"true","reward_percent":"{reward_percent}"}',
            },
          ],
          variables: [
            { name: "id", customName: "crm_contact_id", type: "string" },
          ],
        },
      }),

      // 7. Slack celebrate high-LTV customer
      slackNode(
        "7",
        "Celebrate in #customer-success",
        1480,
        "Star Customer Alert: {customer_name} ({customer_email}) reached {ltv_formatted} LTV!\\nTier: {loyalty_tier} | Orders: {total_orders} | Account age: {account_age_months} months\\nLoyalty app invitation sent.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
    summary: `**High-LTV Customer Loyalty App Prompt** — Identifies high-value customers and invites them to a loyalty program with tier-based rewards.\n\n1. Webhook fires when a customer crosses a high LTV threshold\n2. **AI extracts** customer details: name, email, LTV, order count, plan\n3. **Code node** computes loyalty tier (silver/gold/platinum) and reward percentage\n4. **Email invitation** with personalized loyalty benefits and app download link\n5. **Push notification** reinforcing the loyalty app download\n6. **CRM update** tags the customer with their loyalty tier\n7. **Slack celebration** in #customer-success\n\n**To activate:** Say "connect openai", "connect hubspot", "connect slack", "connect firebase"`,
  },

  // ===================================================================
  // 133. IN-APP ONBOARDING WITH PREFERENCES SURVEY (7 nodes)
  // ===================================================================

  "In App Onboarding with Preferences Survey": {
    nodes: [
      trigger("Webhook: New user signup", "webhook_app", {
        source: "App Backend / Auth0",
        event: "user.created",
        howItStarts:
          "A new user signs up for your app, beginning the onboarding flow with a preferences survey",
        setup:
          "Connect your app backend or Auth0 post-registration webhook to fire when a new user is created. POST the user data to your workflow URL.",
        inputFields: [],
      }),

      // 2. AI extract user details
      aiNode(
        "2",
        "AI extract new user details",
        280,
        "Read this new user signup payload. Extract the user name, email, user ID, signup source (organic, referral, ad, etc.), and device type (mobile, desktop, tablet). Respond JSON: {user_name: string, user_email: string, user_id: string, signup_source: string, device_type: string}",
        "{webhook_payload}",
        [
          { field: "user_name", as: "user_name", type: "string" },
          { field: "user_email", as: "user_email", type: "string" },
          { field: "user_id", as: "user_id", type: "string" },
          { field: "signup_source", as: "signup_source", type: "string" },
          { field: "device_type", as: "device_type", type: "string" },
        ],
      ),

      // 3. Send in-app welcome message
      n("3", "sendMessage", "In-app welcome message", 520, {
        data: {
          title:
            "Welcome to the app, {user_name}! Let's personalize your experience. We'll ask a few quick questions to set things up just for you.",
        },
      }),

      // 4. Trigger preferences survey via API
      n("4", "hitApi", "Trigger preferences survey via Outgrow Forms", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.outgrow-forms.com/forms/{outgrow-forms_form_id}/responses",
          headers: [
            { key: "Authorization", value: "Bearer {outgrow-forms_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "hidden",
              value:
                '{"user_id":"{user_id}","email":"{user_email}","name":"{user_name}"}',
            },
          ],
          variables: [
            { name: "form_id", customName: "survey_form_id", type: "string" },
          ],
        },
      }),

      // 5. Send email with survey link
      emailNode(
        "5",
        "Email: Complete your preferences",
        1000,
        "{user_email}",
        "Quick setup: Tell us what you need, {user_name}",
        "<p>Hi {user_name},</p><p>Welcome aboard! To make your experience great from day one, please take 2 minutes to complete your preferences survey.</p><p>This helps us:</p><ul><li>Show you relevant content and features</li><li>Set up the right notifications</li><li>Personalize your dashboard</li></ul><p><a href='https://form.outgrow-forms.com/to/{survey_form_id}?user_id={user_id}'>Complete Preferences Survey</a></p>",
      ),

      // 6. Delay 1 day
      n("6", "delayAction", "Wait 1 day", 1240, {
        delayAction: { time: 1, timeFormat: "days" },
      }),

      // 7. Push notification reminder
      n("7", "pushNotification", "Push: Complete your setup", 1480, {
        pushNotification: {
          title: "Finish setting up your account",
          body: "Hi {user_name}, complete your quick preferences survey to unlock personalized features!",
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
    summary: `**In-App Onboarding with Preferences Survey** — Guides new users through onboarding with a personalization survey across multiple channels.\n\n1. Webhook fires on new user signup\n2. **AI extracts** user details: name, email, user ID, signup source, device\n3. **In-app message** welcomes the user and introduces the survey\n4. **Outgrow Forms API** triggers the preferences survey with hidden user fields\n5. **Email** with a direct link to complete the survey\n6. **Waits 1 day** for the user to complete the survey\n7. **Push notification** reminder if survey is not yet completed\n\n**To activate:** Say "connect openai", "connect outgrow-forms", "connect firebase"`,
  },

  // ===================================================================
  // 134. INSTALLMENT PAYMENT REMINDER (7 nodes)
  // ===================================================================

  "Installment Payment Reminder": {
    nodes: [
      trigger("Schedule: Daily installment check", "schedule", {
        schedule: "Every day at 8:00 AM",
        howItStarts:
          "Runs daily to check for upcoming installment payments due within 3 days",
        setup:
          "Configure the schedule to run daily. The workflow queries your billing system for installments due in the next 3 days and sends reminders.",
        inputFields: [
          {
            name: "billing_api_base",
            type: "string",
            example: "https://api.billing.example.com",
          },
        ],
      }),

      // 2. Fetch upcoming installments from billing API
      n("2", "hitApi", "Fetch upcoming installments", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.stripe.com/v1/invoices/upcoming?customer={customer_id}&due_date[lte]={three_days_from_now}",
          headers: [
            { key: "Authorization", value: "Bearer {stripe_secret_key}" },
          ],
          variables: [
            {
              name: "data[0].customer_email",
              customName: "customer_email",
              type: "string",
            },
            {
              name: "data[0].customer_name",
              customName: "customer_name",
              type: "string",
            },
            {
              name: "data[0].amount_due",
              customName: "amount_due_cents",
              type: "number",
            },
            {
              name: "data[0].due_date",
              customName: "due_date_unix",
              type: "number",
            },
            { name: "data[0].id", customName: "invoice_id", type: "string" },
            {
              name: "data[0].subscription",
              customName: "subscription_id",
              type: "string",
            },
          ],
        },
      }),

      // 3. Format payment details via code
      n("3", "runCode", "Format payment details", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `const amount = (parseFloat(inputs.amount_due_cents) || 0) / 100;
const dueDate = new Date((parseInt(inputs.due_date_unix) || 0) * 1000);
const formattedDate = dueDate.toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" });
return { amount_formatted: "$" + amount.toFixed(2), due_date_formatted: formattedDate, is_overdue: dueDate < new Date() ? "true" : "false" };`,
        },
      }),

      // 4. Check if overdue
      n("4", "condition", "Is payment overdue?", 760, {
        conditionData: [
          {
            variable: "is_overdue",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Send urgent overdue email (True path)
      emailNode(
        "5",
        "Urgent: Overdue payment email",
        1000,
        "{customer_email}",
        "URGENT: Your installment of {amount_formatted} is overdue",
        "<p>Hi {customer_name},</p><p>Your installment payment of <strong>{amount_formatted}</strong> was due on <strong>{due_date_formatted}</strong> and is now overdue.</p><p>Please make your payment immediately to avoid service interruption.</p><p><a href='https://billing.example.com/pay/{invoice_id}'>Pay Now</a></p><p>If you've already paid, please disregard this message.</p>",
      ),

      // 6. Send upcoming reminder email (False path)
      emailNode(
        "6",
        "Reminder: Upcoming payment email",
        1000,
        "{customer_email}",
        "Reminder: Your installment of {amount_formatted} is due {due_date_formatted}",
        "<p>Hi {customer_name},</p><p>This is a friendly reminder that your next installment payment of <strong>{amount_formatted}</strong> is due on <strong>{due_date_formatted}</strong>.</p><p>Please ensure your payment method is up to date.</p><p><a href='https://billing.example.com/pay/{invoice_id}'>View & Pay Invoice</a></p>",
        460,
      ),

      // 7. Send SMS for overdue
      n("7", "sendSMS", "SMS: Overdue payment alert", 1240, {
        sendSMSDetails: {
          to: "{customer_phone}",
          smsBody:
            "Hi {customer_name}, your installment of {amount_formatted} is overdue. Pay now to avoid interruption: https://billing.example.com/pay/{invoice_id}",
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // True: overdue
      e("4", "6", "output_2"), // False: upcoming
      e("5", "7"),
    ],
    summary: `**Installment Payment Reminder** — Daily scheduled workflow that checks for upcoming and overdue installment payments and sends appropriate reminders.\n\n1. **Scheduled daily** at 8:00 AM to check for installments\n2. **Stripe API** fetches upcoming invoices due within 3 days\n3. **Code node** formats the amount and date, determines if overdue\n4. **Condition** branches on overdue status\n5. **Overdue path**: Urgent email with immediate payment link, followed by SMS alert\n6. **Upcoming path**: Friendly reminder email with payment link\n\n**To activate:** Say "connect stripe", "connect twilio"`,
  },

  // ===================================================================
  // 135. LICENSE-SEAT EXPANSION REMINDER (7 nodes)
  // ===================================================================

  "License Seat Expansion Reminder": {
    nodes: [
      trigger("Schedule: Weekly seat utilization check", "schedule", {
        schedule: "Every Monday at 9:00 AM",
        howItStarts:
          "Runs weekly to check which accounts are approaching their license seat limit",
        setup:
          "Configure the schedule to run weekly. The workflow queries your license management system for accounts at 80%+ seat utilization.",
        inputFields: [
          { name: "utilization_threshold", type: "number", example: "80" },
        ],
      }),

      // 2. Fetch seat utilization from license API
      n("2", "hitApi", "Fetch seat utilization data", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.example.com/v1/accounts?seat_utilization_gte=80",
          headers: [
            { key: "Authorization", value: "Bearer {license_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          variables: [
            {
              name: "data[0].account_name",
              customName: "account_name",
              type: "string",
            },
            {
              name: "data[0].admin_email",
              customName: "admin_email",
              type: "string",
            },
            {
              name: "data[0].admin_name",
              customName: "admin_name",
              type: "string",
            },
            {
              name: "data[0].seats_used",
              customName: "seats_used",
              type: "number",
            },
            {
              name: "data[0].seats_total",
              customName: "seats_total",
              type: "number",
            },
            {
              name: "data[0].plan_name",
              customName: "plan_name",
              type: "string",
            },
            {
              name: "data[0].account_id",
              customName: "account_id",
              type: "string",
            },
          ],
        },
      }),

      // 3. Calculate utilization percentage
      n("3", "runCode", "Calculate seat utilization %", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `const used = parseInt(inputs.seats_used) || 0;
const total = parseInt(inputs.seats_total) || 1;
const utilization = Math.round((used / total) * 100);
const seats_remaining = total - used;
const is_critical = utilization >= 95 ? "true" : "false";
return { utilization_percent: String(utilization), seats_remaining: String(seats_remaining), is_critical };`,
        },
      }),

      // 4. Check if critical (>= 95%)
      n("4", "condition", "Seat utilization >= 95%?", 760, {
        conditionData: [
          {
            variable: "is_critical",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Critical: Urgent expansion email (True path)
      emailNode(
        "5",
        "Urgent: Almost out of seats",
        1000,
        "{admin_email}",
        "URGENT: {account_name} has only {seats_remaining} seats remaining",
        "<p>Hi {admin_name},</p><p>Your <strong>{plan_name}</strong> plan for <strong>{account_name}</strong> is at <strong>{utilization_percent}%</strong> capacity ({seats_used}/{seats_total} seats).</p><p>You only have <strong>{seats_remaining} seats</strong> remaining. New team members won't be able to access the platform once seats run out.</p><p><a href='https://app.example.com/billing/expand/{account_id}'>Expand Your Seats Now</a></p><p>Need help? Reply to this email and we'll assist immediately.</p>",
      ),

      // 6. Standard: Expansion suggestion email (False path)
      emailNode(
        "6",
        "Reminder: Consider expanding seats",
        1000,
        "{admin_email}",
        "{account_name} is at {utilization_percent}% seat utilization",
        "<p>Hi {admin_name},</p><p>Your <strong>{plan_name}</strong> plan for <strong>{account_name}</strong> is at <strong>{utilization_percent}%</strong> capacity ({seats_used}/{seats_total} seats).</p><p>With {seats_remaining} seats remaining, now is a great time to plan for growth. Expand early to ensure seamless onboarding for new team members.</p><p><a href='https://app.example.com/billing/expand/{account_id}'>View Expansion Options</a></p>",
        460,
      ),

      // 7. Notify sales team for critical accounts
      slackNode(
        "7",
        "Alert #sales: Critical seat usage",
        1240,
        "Seat Alert: {account_name} is at {utilization_percent}% ({seats_used}/{seats_total})!\\nAdmin: {admin_name} ({admin_email})\\nPlan: {plan_name} | Seats remaining: {seats_remaining}\\nExpansion email sent. Follow up for upsell.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // True: critical
      e("4", "6", "output_2"), // False: standard
      e("5", "7"),
    ],
    summary: `**License-Seat Expansion Reminder** — Weekly check for accounts approaching seat limits, with urgency-based notifications.\n\n1. **Scheduled weekly** on Mondays to check seat utilization\n2. **License API** fetches accounts at 80%+ utilization\n3. **Code node** calculates exact utilization percentage and remaining seats\n4. **Condition** branches: critical (>= 95%) vs. standard\n5. **Critical path**: Urgent email + Slack alert to sales team\n6. **Standard path**: Friendly expansion suggestion email\n\n**To activate:** Say "connect openai", "connect slack"`,
  },

  // ===================================================================
  // 136. MARKETPLACE BUYER FIRST ORDER (7 nodes)
  // ===================================================================

  "Marketplace Buyer First Order": {
    nodes: [
      trigger("Webhook: First order placed", "webhook_app", {
        source: "Marketplace Backend / Shopify",
        event: "order.created",
        howItStarts: "A buyer places their first order on the marketplace",
        setup:
          "Connect your marketplace backend or Shopify webhook to fire on order.created. The workflow checks if it's the buyer's first order and sends a tailored welcome experience.",
        inputFields: [],
      }),

      // 2. AI extract order details
      aiNode(
        "2",
        "AI extract first order details",
        280,
        "Read this marketplace order webhook payload. Extract buyer name, buyer email, order ID, order total, number of items, seller name, product category, and whether this is the buyer's first order (check order_count or similar field). Respond JSON: {buyer_name: string, buyer_email: string, order_id: string, order_total: string, item_count: string, seller_name: string, product_category: string, is_first_order: string}",
        "{webhook_payload}",
        [
          { field: "buyer_name", as: "buyer_name", type: "string" },
          { field: "buyer_email", as: "buyer_email", type: "string" },
          { field: "order_id", as: "order_id", type: "string" },
          { field: "order_total", as: "order_total", type: "string" },
          { field: "item_count", as: "item_count", type: "string" },
          { field: "seller_name", as: "seller_name", type: "string" },
          { field: "product_category", as: "product_category", type: "string" },
          { field: "is_first_order", as: "is_first_order", type: "string" },
        ],
      ),

      // 3. Condition: Is first order?
      n("3", "condition", "Is this the buyer's first order?", 520, {
        conditionData: [
          {
            variable: "is_first_order",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. Welcome email with marketplace guide
      emailNode(
        "4",
        "Welcome email: First order guide",
        760,
        "{buyer_email}",
        "Welcome to the marketplace, {buyer_name}! Your first order is confirmed",
        "<p>Hi {buyer_name},</p><p>Congratulations on your first order (#{order_id})!</p><p>Here's what happens next:</p><ul><li>Your seller <strong>{seller_name}</strong> has been notified and will ship your {item_count} item(s) soon</li><li>You'll receive tracking info via email</li><li>If anything goes wrong, our buyer protection has you covered</li></ul><p><strong>Pro tips for new buyers:</strong></p><ul><li>Leave a review after delivery to help other buyers</li><li>Save favorite sellers for quick reordering</li><li>Check out more in <strong>{product_category}</strong></li></ul><p><a href='https://marketplace.example.com/orders/{order_id}'>Track Your Order</a></p>",
      ),

      // 5. In-app welcome message
      n("5", "sendMessage", "In-app: Welcome first-time buyer", 1000, {
        data: {
          title:
            "Welcome to the marketplace, {buyer_name}! Your order #{order_id} from {seller_name} is confirmed. Here's a quick guide to getting the most out of your buying experience.",
        },
      }),

      // 6. Push notification with discount for second order
      n("6", "pushNotification", "Push: Second order discount", 1240, {
        pushNotification: {
          title: "Thanks for your first order!",
          body: "Hi {buyer_name}, enjoy 10% off your next purchase with code WELCOME10. Explore more in {product_category}!",
        },
      }),

      // 7. Log first buyer event to analytics
      n("7", "hitApi", "Log first buyer event to Segment", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.segment.io/v1/track",
          headers: [
            { key: "Authorization", value: "Basic {segment_write_key_b64}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "userId", value: "{buyer_email}" },
            { key: "event", value: "First Order Placed" },
            {
              key: "properties",
              value:
                '{"order_id":"{order_id}","order_total":"{order_total}","item_count":"{item_count}","seller_name":"{seller_name}","product_category":"{product_category}"}',
            },
          ],
          variables: [
            { name: "success", customName: "segment_tracked", type: "boolean" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // True: first order
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
    summary: `**Marketplace Buyer First Order** — Welcomes first-time marketplace buyers with a guided onboarding experience and retention incentive.\n\n1. Webhook fires when a buyer places an order\n2. **AI extracts** order details and checks if it's the buyer's first order\n3. **Condition** gates the flow to only proceed for first-time buyers\n4. **Welcome email** with order details, seller info, and marketplace tips\n5. **In-app message** with personalized welcome\n6. **Push notification** with a 10% discount code for the second order\n7. **Segment tracking** logs the first-order event for analytics\n\n**To activate:** Say "connect openai", "connect segment", "connect firebase"`,
  },

  // ===================================================================
  // 137. MARKETPLACE SELLER FIRST LISTING (8 nodes)
  // ===================================================================

  "Marketplace Seller First Listing": {
    nodes: [
      trigger("Webhook: First listing created", "webhook_app", {
        source: "Marketplace Backend",
        event: "listing.created",
        howItStarts:
          "A seller creates their first product listing on the marketplace",
        setup:
          "Connect your marketplace backend webhook to fire on listing.created. The workflow checks if it's the seller's first listing and orchestrates a guided seller onboarding.",
        inputFields: [],
      }),

      // 2. AI extract listing details
      aiNode(
        "2",
        "AI extract first listing details",
        280,
        "Read this marketplace listing webhook payload. Extract seller name, seller email, listing title, listing price, category, listing ID, seller ID, and total listing count for this seller. Respond JSON: {seller_name: string, seller_email: string, listing_title: string, listing_price: string, listing_category: string, listing_id: string, seller_id: string, total_listings: string}",
        "{webhook_payload}",
        [
          { field: "seller_name", as: "seller_name", type: "string" },
          { field: "seller_email", as: "seller_email", type: "string" },
          { field: "listing_title", as: "listing_title", type: "string" },
          { field: "listing_price", as: "listing_price", type: "string" },
          { field: "listing_category", as: "listing_category", type: "string" },
          { field: "listing_id", as: "listing_id", type: "string" },
          { field: "seller_id", as: "seller_id", type: "string" },
          { field: "total_listings", as: "total_listings", type: "string" },
        ],
      ),

      // 3. Condition: Is first listing?
      n("3", "condition", "Is this the seller's first listing?", 520, {
        conditionData: [
          {
            variable: "total_listings",
            value: "1",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. AI review listing quality
      aiNode(
        "4",
        "AI review listing quality",
        760,
        "You are a marketplace listing quality reviewer. Review this listing for completeness and suggest improvements. Check title clarity, pricing reasonableness for the category, and description quality. Respond JSON: {quality_score: number, title_feedback: string, price_feedback: string, improvement_tips: string, is_approved: string}",
        "Title: {listing_title}, Price: {listing_price}, Category: {listing_category}, Seller: {seller_name}",
        [
          { field: "quality_score", as: "quality_score", type: "number" },
          { field: "title_feedback", as: "title_feedback", type: "string" },
          { field: "price_feedback", as: "price_feedback", type: "string" },
          { field: "improvement_tips", as: "improvement_tips", type: "string" },
          { field: "is_approved", as: "is_approved", type: "string" },
        ],
      ),

      // 5. Approval gate for marketplace admins
      n("5", "approvalGate", "Admin review first listing", 1000, {
        approvalGateData: {
          message:
            "New seller {seller_name} created their first listing:\\n\\nTitle: {listing_title}\\nPrice: {listing_price}\\nCategory: {listing_category}\\n\\nAI Quality Score: {quality_score}/100\\nTitle feedback: {title_feedback}\\nPrice feedback: {price_feedback}\\nTips: {improvement_tips}\\n\\nApprove to publish and send welcome sequence, or reject to request changes.",
        },
      }),

      // 6. Welcome email with seller success guide
      emailNode(
        "6",
        "Welcome email: Seller success guide",
        1240,
        "{seller_email}",
        "Your first listing is live! Here's how to get your first sale, {seller_name}",
        "<p>Hi {seller_name},</p><p>Congratulations! Your listing <strong>{listing_title}</strong> is now live on the marketplace.</p><p><strong>Tips to get your first sale:</strong></p><ul><li>Add high-quality photos (listings with 5+ photos sell 3x faster)</li><li>Write a detailed description with keywords buyers search for</li><li>Price competitively in the <strong>{listing_category}</strong> category</li><li>Respond to buyer questions within 1 hour</li></ul><p><a href='https://marketplace.example.com/seller/dashboard/{seller_id}'>View Your Seller Dashboard</a></p>",
      ),

      // 7. Notify seller success team
      slackNode(
        "7",
        "Notify #seller-success: New seller",
        1480,
        "New Seller: {seller_name} ({seller_email}) created their first listing!\\nListing: {listing_title} at {listing_price} in {listing_category}\\nAI Quality: {quality_score}/100\\nID: {seller_id}",
      ),

      // 8. Track in analytics
      n("8", "hitApi", "Track first listing event in Segment", 1720, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.segment.io/v1/track",
          headers: [
            { key: "Authorization", value: "Basic {segment_write_key_b64}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "userId", value: "{seller_id}" },
            { key: "event", value: "First Listing Created" },
            {
              key: "properties",
              value:
                '{"listing_id":"{listing_id}","listing_title":"{listing_title}","listing_price":"{listing_price}","listing_category":"{listing_category}","quality_score":"{quality_score}"}',
            },
          ],
          variables: [
            { name: "success", customName: "segment_tracked", type: "boolean" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // True: first listing
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary: `**Marketplace Seller First Listing** — Guides new sellers through their first listing with AI quality review, admin approval, and onboarding.\n\n1. Webhook fires when a seller creates a listing\n2. **AI extracts** listing and seller details\n3. **Condition** gates the flow to first-time sellers only\n4. **AI reviews** listing quality — title, pricing, completeness\n5. **Admin approval gate** for marketplace quality control\n6. **Welcome email** with seller success tips and dashboard link\n7. **Slack notification** to #seller-success team\n8. **Segment tracking** for first-listing analytics\n\n**To activate:** Say "connect openai", "connect segment", "connect slack"`,
  },

  // ===================================================================
  // 138. MOBILE APP ONBOARDING WITH PUSH AND IN-APP (8 nodes)
  // ===================================================================

  "Mobile App Onboarding with Push and In App": {
    nodes: [
      trigger("Webhook: Mobile app first open", "webhook_app", {
        source: "Firebase / Mixpanel / App Backend",
        event: "app.first_open",
        howItStarts:
          "A user opens the mobile app for the first time after installation",
        setup:
          "Connect your mobile analytics platform (Firebase, Mixpanel) or app backend to fire a webhook on first app open. Include device info and user data in the payload.",
        inputFields: [],
      }),

      // 2. AI extract user + device details
      aiNode(
        "2",
        "AI extract mobile user details",
        280,
        "Read this mobile app first open payload. Extract user name, email, device type (iOS/Android), app version, OS version, and referral source if any. Respond JSON: {user_name: string, user_email: string, device_type: string, app_version: string, os_version: string, referral_source: string}",
        "{webhook_payload}",
        [
          { field: "user_name", as: "user_name", type: "string" },
          { field: "user_email", as: "user_email", type: "string" },
          { field: "device_type", as: "device_type", type: "string" },
          { field: "app_version", as: "app_version", type: "string" },
          { field: "os_version", as: "os_version", type: "string" },
          { field: "referral_source", as: "referral_source", type: "string" },
        ],
      ),

      // 3. In-app welcome message
      n("3", "sendMessage", "In-app: Welcome tour prompt", 520, {
        data: {
          title:
            "Welcome to the app, {user_name}! Let's take a quick 30-second tour to help you get the most out of your experience. Tap 'Start Tour' to begin.",
        },
      }),

      // 4. Push notification: Enable notifications
      n("4", "pushNotification", "Push: Enable notification permissions", 760, {
        pushNotification: {
          title: "Stay in the loop!",
          body: "Hi {user_name}, enable notifications to get updates on what matters most to you.",
        },
      }),

      // 5. Delay 1 hour
      n("5", "delayAction", "Wait 1 hour", 1000, {
        delayAction: { time: 1, timeFormat: "hours" },
      }),

      // 6. In-app: Feature highlight
      n("6", "sendMessage", "In-app: Key feature highlight", 1240, {
        data: {
          title:
            "Did you know? You can customize your dashboard by tapping the gear icon. Set up your preferences to see the content you care about most!",
        },
      }),

      // 7. Delay 1 day
      n("7", "delayAction", "Wait 1 day", 1480, {
        delayAction: { time: 1, timeFormat: "days" },
      }),

      // 8. Email: Onboarding completion check
      emailNode(
        "8",
        "Email: Complete your setup",
        1720,
        "{user_email}",
        "Get the most from your app, {user_name}",
        "<p>Hi {user_name},</p><p>We noticed you started using the app on your <strong>{device_type}</strong>. Here are a few things to make your experience even better:</p><ul><li>Complete your profile for personalized recommendations</li><li>Connect your accounts for seamless sync</li><li>Explore the discover tab to find trending content</li></ul><p>Questions? Just reply to this email or use the in-app help button.</p><p><a href='https://app.example.com/onboarding/complete'>Finish Setup</a></p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary: `**Mobile App Onboarding with Push and In-App** — Multi-step mobile onboarding using push notifications, in-app messages, and email over the first 24 hours.\n\n1. Webhook fires on mobile app first open\n2. **AI extracts** user details and device information\n3. **In-app message** prompts the user to take a quick tour\n4. **Push notification** encourages enabling notification permissions\n5. **Waits 1 hour** for user to explore\n6. **In-app message** highlights a key feature\n7. **Waits 1 day** for ongoing engagement\n8. **Email** checks in and suggests completing setup\n\n**To activate:** Say "connect openai", "connect firebase"`,
  },

  // ===================================================================
  // 139. MULTI AGENT TASK ORCHESTRATOR (8 nodes)
  // ===================================================================

  "Multi Agent Task Orchestrator": {
    nodes: [
      trigger("Webhook: Complex task submitted", "webhook_app", {
        source: "App Backend / Slack Command",
        event: "task.submitted",
        howItStarts:
          "A complex task is submitted that requires multiple AI agents to collaborate",
        setup:
          "POST a task description to your workflow URL. The orchestrator will decompose it into subtasks, route to specialized agents, aggregate results, and deliver the final output.",
        inputFields: [],
      }),

      // 2. AI decompose task into subtasks
      aiNode(
        "2",
        "Orchestrator: Decompose task",
        280,
        "You are a task orchestrator. Break this complex task into 2-4 distinct subtasks that can be handled by specialized agents. Identify the type of each subtask: research, analysis, writing, or coding. Respond JSON: {task_summary: string, subtask_1: string, subtask_1_type: string, subtask_2: string, subtask_2_type: string, subtask_3: string, subtask_3_type: string, total_subtasks: string}",
        "{webhook_payload}",
        [
          { field: "task_summary", as: "task_summary", type: "string" },
          { field: "subtask_1", as: "subtask_1", type: "string" },
          { field: "subtask_1_type", as: "subtask_1_type", type: "string" },
          { field: "subtask_2", as: "subtask_2", type: "string" },
          { field: "subtask_2_type", as: "subtask_2_type", type: "string" },
          { field: "subtask_3", as: "subtask_3", type: "string" },
          { field: "subtask_3_type", as: "subtask_3_type", type: "string" },
          { field: "total_subtasks", as: "total_subtasks", type: "string" },
        ],
      ),

      // 3. Agent 1: Research specialist
      aiNode(
        "3",
        "Agent 1: Research specialist",
        520,
        "You are a research specialist agent. Thoroughly research and gather information for the given subtask. Be detailed and cite any assumptions. Respond JSON: {research_result: string, confidence: number, sources_count: number}",
        "Subtask: {subtask_1}\\nContext: {task_summary}",
        [
          { field: "research_result", as: "research_result", type: "string" },
          { field: "confidence", as: "research_confidence", type: "number" },
          { field: "sources_count", as: "research_sources", type: "number" },
        ],
      ),

      // 4. Agent 2: Analysis specialist
      aiNode(
        "4",
        "Agent 2: Analysis specialist",
        520,
        "You are an analysis specialist agent. Analyze the data and provide insights for the given subtask. Use structured reasoning and quantify findings where possible. Respond JSON: {analysis_result: string, confidence: number, key_insights_count: number}",
        "Subtask: {subtask_2}\\nContext: {task_summary}\\nResearch data: {research_result}",
        [
          { field: "analysis_result", as: "analysis_result", type: "string" },
          { field: "confidence", as: "analysis_confidence", type: "number" },
          {
            field: "key_insights_count",
            as: "analysis_insights",
            type: "number",
          },
        ],
        460,
      ),

      // 5. Agent 3: Writing/synthesis specialist
      aiNode(
        "5",
        "Agent 3: Writer/synthesizer",
        760,
        "You are a writing and synthesis specialist agent. Combine the research and analysis into a clear, well-structured deliverable for the given subtask. Respond JSON: {written_output: string, word_count: number, quality_score: number}",
        "Subtask: {subtask_3}\\nContext: {task_summary}\\nResearch: {research_result}\\nAnalysis: {analysis_result}",
        [
          { field: "written_output", as: "written_output", type: "string" },
          { field: "word_count", as: "word_count", type: "number" },
          { field: "quality_score", as: "quality_score", type: "number" },
        ],
      ),

      // 6. Orchestrator: Merge and validate
      aiNode(
        "6",
        "Orchestrator: Merge and validate",
        1000,
        "You are the task orchestrator. Merge all agent outputs into a final cohesive deliverable. Validate consistency, fill any gaps, and produce a polished result. Respond JSON: {final_output: string, overall_quality: number, agents_used: number, total_confidence: number}",
        "Task: {task_summary}\\nResearch (confidence: {research_confidence}): {research_result}\\nAnalysis (confidence: {analysis_confidence}): {analysis_result}\\nWritten (quality: {quality_score}): {written_output}",
        [
          { field: "final_output", as: "final_output", type: "string" },
          { field: "overall_quality", as: "overall_quality", type: "number" },
          { field: "agents_used", as: "agents_used", type: "number" },
          { field: "total_confidence", as: "total_confidence", type: "number" },
        ],
      ),

      // 7. Approval gate for human review
      n("7", "approvalGate", "Human review: Final output", 1240, {
        approvalGateData: {
          message:
            "Multi-agent task completed:\\n\\nTask: {task_summary}\\nAgents used: {agents_used}\\nOverall quality: {overall_quality}/100\\nTotal confidence: {total_confidence}%\\n\\nFinal output preview:\\n{final_output}\\n\\nApprove to deliver, reject to re-run agents.",
        },
      }),

      // 8. Deliver result via Slack
      slackNode(
        "8",
        "Deliver final output via Slack",
        1480,
        "Multi-Agent Task Complete\\n\\nTask: {task_summary}\\nQuality: {overall_quality}/100 | Confidence: {total_confidence}%\\n\\nResult:\\n{final_output}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("2", "4"),
      e("3", "5"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary: `**Multi-Agent Task Orchestrator** — Decomposes complex tasks into subtasks, routes them to specialized AI agents, merges results, and delivers a polished output.\n\n1. Webhook receives a complex task description\n2. **Orchestrator AI** decomposes the task into subtasks with type labels\n3. **Research Agent** gathers information for subtask 1\n4. **Analysis Agent** (parallel) analyzes data for subtask 2\n5. **Writer Agent** synthesizes research and analysis for subtask 3\n6. **Orchestrator** merges all agent outputs into a final deliverable\n7. **Human approval gate** for quality review before delivery\n8. **Slack delivery** of the final output\n\n**To activate:** Say "connect openai", "connect slack"`,
  },

  // ===================================================================
  // 140. MULTI LLM CONTENT QUALITY CHECKER (8 nodes)
  // ===================================================================

  "Multi LLM Content Quality Checker": {
    nodes: [
      trigger("Manual: Submit content for quality check", "manual", {
        howItStarts:
          "You submit content text for multi-LLM quality verification",
        setup:
          "POST the content to be checked along with the content type (blog, email, ad copy, documentation). The workflow runs the content through multiple LLMs and compares quality scores.",
        inputFields: [
          {
            name: "content_text",
            type: "string",
            example: "Your blog post or marketing copy text here...",
          },
          { name: "content_type", type: "string", example: "blog_post" },
          {
            name: "target_audience",
            type: "string",
            example: "B2B SaaS decision makers",
          },
          { name: "reviewer_email", type: "string" },
        ],
      }),

      // 2. LLM 1: OpenAI quality assessment
      aiNode(
        "2",
        "LLM 1: OpenAI quality check",
        280,
        "You are a content quality assessor. Evaluate this content on: clarity (0-25), engagement (0-25), accuracy (0-25), and tone appropriateness (0-25). Total score is 0-100. Also identify grammar issues, weak sentences, and suggest improvements. Respond JSON: {clarity_score: number, engagement_score: number, accuracy_score: number, tone_score: number, total_score: number, grammar_issues: string, weak_sentences: string, improvements: string}",
        "Content type: {content_type}\\nTarget audience: {target_audience}\\nContent:\\n{content_text}",
        [
          { field: "total_score", as: "openai_total_score", type: "number" },
          { field: "clarity_score", as: "openai_clarity", type: "number" },
          {
            field: "engagement_score",
            as: "openai_engagement",
            type: "number",
          },
          {
            field: "grammar_issues",
            as: "openai_grammar_issues",
            type: "string",
          },
          { field: "improvements", as: "openai_improvements", type: "string" },
        ],
      ),

      // 3. LLM 2: Anthropic quality assessment
      n(
        "3",
        "hitApi",
        "LLM 2: Anthropic quality check",
        280,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.anthropic.com/v1/messages",
            headers: [
              { key: "x-api-key", value: "{anthropic_key}" },
              { key: "Content-Type", value: "application/json" },
              { key: "anthropic-version", value: "2023-06-01" },
            ],
            body: [
              { key: "model", value: "claude-sonnet-4-20250514" },
              { key: "max_tokens", value: "1024" },
              {
                key: "messages",
                value: `[{"role":"user","content":"Evaluate this ${"{content_type}"} content for ${"{target_audience}"}. Score: clarity (0-25), engagement (0-25), accuracy (0-25), tone (0-25). Identify issues. Respond JSON only: {total_score: number, clarity_score: number, engagement_score: number, accuracy_score: number, tone_score: number, key_issues: string, rewrite_suggestions: string}\\n\\nContent:\\n${"{content_text}"}"}]`,
              },
            ],
            variables: [
              {
                name: "content[0].text",
                customName: "anthropic_raw_response",
                type: "string",
              },
            ],
          },
        },
        460,
      ),

      // 4. Parse and compare results
      n("4", "runCode", "Compare multi-LLM scores", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `const openaiScore = parseInt(inputs.openai_total_score) || 0;
let anthropicScore = 0;
let anthropicIssues = "";
try {
  const parsed = JSON.parse(inputs.anthropic_raw_response || "{}");
  anthropicScore = parsed.total_score || 0;
  anthropicIssues = parsed.key_issues || "";
} catch(e) { anthropicScore = 0; }
const avgScore = Math.round((openaiScore + anthropicScore) / 2);
const scoreDiff = Math.abs(openaiScore - anthropicScore);
const consensus = scoreDiff <= 15 ? "high" : "low";
const needs_review = avgScore < 70 || consensus === "low" ? "true" : "false";
return { avg_score: String(avgScore), score_diff: String(scoreDiff), consensus, needs_review, openai_score: String(openaiScore), anthropic_score: String(anthropicScore), anthropic_issues: anthropicIssues };`,
        },
      }),

      // 5. Condition: Needs human review?
      n(
        "5",
        "condition",
        "Needs human review? (avg < 70 or low consensus)",
        760,
        {
          conditionData: [
            {
              variable: "needs_review",
              value: "true",
              comparison_operator: "EQUAL TO",
              logical_operator: "AND",
            },
          ],
        },
      ),

      // 6. Approval gate for low-quality content (True path)
      n("6", "approvalGate", "Editor review: Low quality content", 1000, {
        approvalGateData: {
          message:
            "Content quality check flagged for review:\\n\\nContent type: {content_type}\\nAvg score: {avg_score}/100 (OpenAI: {openai_score}, Anthropic: {anthropic_score})\\nConsensus: {consensus} (diff: {score_diff} pts)\\n\\nOpenAI issues: {openai_grammar_issues}\\nAnthropic issues: {anthropic_issues}\\n\\nOpenAI suggestions: {openai_improvements}\\n\\nApprove to publish as-is, or reject to send back for revisions.",
        },
      }),

      // 7. Email: Quality report (both paths merge here)
      emailNode(
        "7",
        "Email: Content quality report",
        1240,
        "{reviewer_email}",
        "Content Quality Report: {content_type} scored {avg_score}/100",
        "<p>Hi,</p><p>Your <strong>{content_type}</strong> has been evaluated by multiple AI models.</p><h3>Scores</h3><ul><li>OpenAI: <strong>{openai_score}/100</strong> (Clarity: {openai_clarity}, Engagement: {openai_engagement})</li><li>Anthropic: <strong>{anthropic_score}/100</strong></li><li>Average: <strong>{avg_score}/100</strong></li><li>Model consensus: <strong>{consensus}</strong> (difference: {score_diff} pts)</li></ul><h3>Issues Found</h3><p>{openai_grammar_issues}</p><p>{anthropic_issues}</p><h3>Suggested Improvements</h3><p>{openai_improvements}</p>",
      ),

      // 8. Slack summary
      slackNode(
        "8",
        "Slack: Quality check summary",
        1480,
        "Content Quality Check Complete\\nType: {content_type} | Audience: {target_audience}\\nScores: OpenAI {openai_score} / Anthropic {anthropic_score} / Avg {avg_score}\\nConsensus: {consensus} | Review needed: {needs_review}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("1", "3"),
      e("2", "4"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // True: needs review
      e("5", "7", "output_2"), // False: passes quality
      e("6", "7"),
      e("7", "8"),
    ],
    summary: `**Multi-LLM Content Quality Checker** — Runs content through multiple LLMs (OpenAI + Anthropic) in parallel, compares scores, and flags low-quality or low-consensus content for review.\n\n1. Submit content text, type, and target audience\n2. **OpenAI** evaluates clarity, engagement, accuracy, tone\n3. **Anthropic** (parallel) provides independent quality assessment\n4. **Code node** compares scores, computes average and consensus\n5. **Condition** flags content scoring below 70 or with low model consensus\n6. **Approval gate** for editor review of flagged content\n7. **Email report** with detailed scores and improvement suggestions\n8. **Slack summary** for the content team\n\n**To activate:** Say "connect openai", "connect anthropic", "connect slack"`,
  },

  // ===================================================================
  // 141. MULTI MODAL CONTENT GENERATOR (9 nodes)
  // ===================================================================

  "Multi Modal Content Generator": {
    nodes: [
      trigger("Manual: Content generation brief", "manual", {
        howItStarts:
          "You submit a content brief to generate text, images, and social media posts",
        setup:
          "Provide a topic, target audience, tone, and desired output formats. The workflow generates blog content, social media posts, and images from a single brief.",
        inputFields: [
          {
            name: "topic",
            type: "string",
            example: "AI-powered customer service automation",
          },
          {
            name: "target_audience",
            type: "string",
            example: "SaaS founders and CX leaders",
          },
          {
            name: "tone",
            type: "string",
            example: "professional yet approachable",
          },
          { name: "brand_name", type: "string", example: "OmniEngage" },
          { name: "editor_email", type: "string" },
        ],
      }),

      // 2. AI: Generate blog outline + key points
      aiNode(
        "2",
        "AI: Generate content outline",
        280,
        "You are a content strategist. Create a detailed blog outline with 4-5 sections, key points for each, a compelling headline, meta description, and 3 social media angles. Respond JSON: {headline: string, meta_description: string, outline_sections: string, key_statistics: string, social_angle_1: string, social_angle_2: string, social_angle_3: string}",
        "Topic: {topic}\\nAudience: {target_audience}\\nTone: {tone}\\nBrand: {brand_name}",
        [
          { field: "headline", as: "headline", type: "string" },
          { field: "meta_description", as: "meta_description", type: "string" },
          { field: "outline_sections", as: "outline_sections", type: "string" },
          { field: "key_statistics", as: "key_statistics", type: "string" },
          { field: "social_angle_1", as: "social_angle_1", type: "string" },
          { field: "social_angle_2", as: "social_angle_2", type: "string" },
          { field: "social_angle_3", as: "social_angle_3", type: "string" },
        ],
      ),

      // 3. AI: Write full blog post
      aiNode(
        "3",
        "AI: Write full blog post",
        520,
        "You are an expert content writer. Write a complete 800-1200 word blog post based on the outline. Use a {tone} tone targeting {target_audience}. Include a strong intro hook, actionable advice, and a clear CTA. Respond JSON: {blog_body: string, word_count: number, cta_text: string, estimated_read_time: string}",
        "Headline: {headline}\\nOutline: {outline_sections}\\nStats: {key_statistics}\\nBrand: {brand_name}\\nTone: {tone}\\nAudience: {target_audience}",
        [
          { field: "blog_body", as: "blog_body", type: "string" },
          { field: "word_count", as: "word_count", type: "number" },
          { field: "cta_text", as: "cta_text", type: "string" },
          {
            field: "estimated_read_time",
            as: "estimated_read_time",
            type: "string",
          },
        ],
      ),

      // 4. AI: Generate social media posts
      aiNode(
        "4",
        "AI: Generate social media posts",
        520,
        "You are a social media expert. Write 3 platform-optimized posts: one for LinkedIn (professional, 150 words max), one for Twitter/X (280 chars max with hashtags), and one for Instagram (engaging caption with emojis). Respond JSON: {linkedin_post: string, twitter_post: string, instagram_post: string}",
        "Topic: {topic}\\nHeadline: {headline}\\nAngles: {social_angle_1}, {social_angle_2}, {social_angle_3}\\nBrand: {brand_name}\\nTone: {tone}",
        [
          { field: "linkedin_post", as: "linkedin_post", type: "string" },
          { field: "twitter_post", as: "twitter_post", type: "string" },
          { field: "instagram_post", as: "instagram_post", type: "string" },
        ],
        460,
      ),

      // 5. Generate image via DALL-E
      n("5", "hitApi", "Generate featured image via DALL-E", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.openai.com/v1/images/generations",
          headers: [
            { key: "Authorization", value: "Bearer {openai_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "model", value: "dall-e-3" },
            {
              key: "prompt",
              value:
                "Professional blog featured image for an article titled '{headline}' about {topic}. Clean, modern design with subtle {brand_name} branding. No text in the image.",
            },
            { key: "n", value: "1" },
            { key: "size", value: "1792x1024" },
            { key: "quality", value: "hd" },
          ],
          variables: [
            {
              name: "data[0].url",
              customName: "featured_image_url",
              type: "string",
            },
            {
              name: "data[0].revised_prompt",
              customName: "image_prompt_used",
              type: "string",
            },
          ],
        },
      }),

      // 6. Assemble content package (code node)
      n("6", "runCode", "Assemble content package", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `const pkg = {
  blog: {
    headline: inputs.headline || "",
    meta: inputs.meta_description || "",
    body: inputs.blog_body || "",
    word_count: inputs.word_count || "0",
    read_time: inputs.estimated_read_time || "",
    cta: inputs.cta_text || "",
    image: inputs.featured_image_url || ""
  },
  social: {
    linkedin: inputs.linkedin_post || "",
    twitter: inputs.twitter_post || "",
    instagram: inputs.instagram_post || ""
  }
};
return { content_package: JSON.stringify(pkg), total_assets: "5", package_ready: "true" };`,
        },
      }),

      // 7. Approval gate for editor
      n("7", "approvalGate", "Editor review: Content package", 1240, {
        approvalGateData: {
          message:
            "Content package ready for review:\\n\\nHeadline: {headline}\\nWord count: {word_count} | Read time: {estimated_read_time}\\nFeatured image: {featured_image_url}\\n\\nBlog CTA: {cta_text}\\n\\nLinkedIn preview: {linkedin_post}\\nTwitter preview: {twitter_post}\\n\\nApprove to deliver the package, or reject for revisions.",
        },
      }),

      // 8. Email: Deliver content package
      emailNode(
        "8",
        "Email: Deliver content package",
        1480,
        "{editor_email}",
        "Content Package Ready: {headline}",
        "<p>Your multi-modal content package is ready!</p><h3>Blog Post</h3><p><strong>{headline}</strong></p><p>{meta_description}</p><p>Word count: {word_count} | Read time: {estimated_read_time}</p><p><a href='{featured_image_url}'>Featured Image</a></p><h3>Social Posts</h3><p><strong>LinkedIn:</strong> {linkedin_post}</p><p><strong>Twitter:</strong> {twitter_post}</p><p><strong>Instagram:</strong> {instagram_post}</p><p>CTA: {cta_text}</p>",
      ),

      // 9. Slack: Content package delivered
      slackNode(
        "9",
        "Slack: Content package delivered",
        1720,
        "Content Package Delivered\\nHeadline: {headline}\\nWord count: {word_count} | Assets: {total_assets}\\nBlog + LinkedIn + Twitter + Instagram + Featured Image\\nReview: check email for full package.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("2", "4"),
      e("3", "5"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
    summary: `**Multi-Modal Content Generator** — Generates a complete content package (blog + social + image) from a single brief, with editor approval.\n\n1. Submit a content brief with topic, audience, tone, and brand\n2. **AI strategist** creates outline, key points, and social angles\n3. **AI writer** produces a full 800-1200 word blog post\n4. **AI social expert** (parallel) creates LinkedIn, Twitter/X, and Instagram posts\n5. **DALL-E** generates a professional featured image\n6. **Code node** assembles the complete content package\n7. **Editor approval gate** for quality review\n8. **Email delivery** with the full content package\n9. **Slack notification** that the package is ready\n\n**To activate:** Say "connect openai", "connect slack"`,
  },

  // ===================================================================
  // 142. NATURAL LANGUAGE DATABASE QUERY AGENT (8 nodes)
  // ===================================================================

  "Natural Language Database Query Agent": {
    nodes: [
      trigger("Manual: Ask a database question", "manual", {
        howItStarts:
          "A user asks a question in natural language to query a database",
        setup:
          "Type a question in plain English (e.g., 'How many orders were placed last month?' or 'Who are our top 10 customers by revenue?'). The agent translates it to SQL, validates it, executes it, and returns a formatted answer.",
        inputFields: [
          {
            name: "user_question",
            type: "string",
            example: "What were the top 5 products sold last quarter?",
          },
          {
            name: "database_schema",
            type: "string",
            example:
              "Tables: orders(id, customer_id, product_id, amount, created_at), customers(id, name, email), products(id, name, category, price)",
          },
          { name: "requester_email", type: "string" },
        ],
      }),

      // 2. AI: Translate natural language to SQL
      aiNode(
        "2",
        "AI: Natural language to SQL",
        280,
        "You are a SQL expert. Translate this natural language question into a safe, read-only SQL query. Only generate SELECT statements — never INSERT, UPDATE, DELETE, DROP, or ALTER. Include appropriate JOINs, GROUP BYs, and ORDER BYs. Respond JSON: {sql_query: string, explanation: string, tables_used: string, is_safe: string, estimated_complexity: string}",
        "Question: {user_question}\\nDatabase schema: {database_schema}",
        [
          { field: "sql_query", as: "sql_query", type: "string" },
          { field: "explanation", as: "query_explanation", type: "string" },
          { field: "tables_used", as: "tables_used", type: "string" },
          { field: "is_safe", as: "is_safe_query", type: "string" },
          {
            field: "estimated_complexity",
            as: "query_complexity",
            type: "string",
          },
        ],
      ),

      // 3. Safety check: is the query read-only?
      n("3", "condition", "Is query safe (read-only)?", 520, {
        conditionData: [
          {
            variable: "is_safe_query",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. Execute query via database API
      n("4", "hitApi", "Execute SQL query", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.example.com/v1/database/query",
          headers: [
            { key: "Authorization", value: "Bearer {database_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "query", value: "{sql_query}" },
            { key: "read_only", value: "true" },
            { key: "timeout_ms", value: "30000" },
          ],
          variables: [
            { name: "data", customName: "query_results", type: "string" },
            { name: "row_count", customName: "result_count", type: "number" },
            {
              name: "execution_time_ms",
              customName: "execution_time",
              type: "number",
            },
          ],
        },
      }),

      // 5. AI: Format results into natural language answer
      aiNode(
        "5",
        "AI: Format results as answer",
        1000,
        "You are a data analyst. Take these SQL query results and format them into a clear, natural language answer to the user's original question. Include relevant numbers, percentages, and trends. If the results are tabular, format as a clean table. Respond JSON: {answer: string, summary: string, has_data: string, insights: string}",
        "Original question: {user_question}\\nSQL used: {sql_query}\\nResults ({result_count} rows, {execution_time}ms):\\n{query_results}",
        [
          { field: "answer", as: "formatted_answer", type: "string" },
          { field: "summary", as: "answer_summary", type: "string" },
          { field: "has_data", as: "has_data", type: "string" },
          { field: "insights", as: "data_insights", type: "string" },
        ],
      ),

      // 6. Send answer via in-app message
      n("6", "sendMessage", "Deliver answer in-app", 1240, {
        data: {
          title:
            "Answer: {answer_summary}\\n\\n{formatted_answer}\\n\\nInsights: {data_insights}\\n\\n(Query: {sql_query} — {result_count} rows in {execution_time}ms)",
        },
      }),

      // 7. Email the full report
      emailNode(
        "7",
        "Email: Query results report",
        1480,
        "{requester_email}",
        "Query Results: {answer_summary}",
        "<p>Hi,</p><p>Here are the results for your question: <em>{user_question}</em></p><h3>Answer</h3><p>{formatted_answer}</p><h3>Key Insights</h3><p>{data_insights}</p><h3>Technical Details</h3><ul><li>SQL: <code>{sql_query}</code></li><li>Tables: {tables_used}</li><li>Rows returned: {result_count}</li><li>Execution time: {execution_time}ms</li><li>Complexity: {query_complexity}</li></ul>",
      ),

      // 8. Reject path: Unsafe query notification
      emailNode(
        "8",
        "Notify: Unsafe query rejected",
        520,
        "{requester_email}",
        "Query Rejected: Safety check failed",
        "<p>Hi,</p><p>Your question <em>{user_question}</em> was translated into a query that was flagged as potentially unsafe.</p><p>Generated SQL: <code>{sql_query}</code></p><p>Explanation: {query_explanation}</p><p>Please rephrase your question to only request data retrieval (no modifications). If you believe this is an error, contact the data team.</p>",
        460,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // True: safe query
      e("3", "8", "output_2"), // False: unsafe query
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
    summary: `**Natural Language Database Query Agent** — Translates plain English questions into SQL, validates safety, executes, and returns formatted answers.\n\n1. User asks a question in natural language\n2. **AI translates** the question into a safe, read-only SQL query\n3. **Safety check** ensures the query is SELECT-only (no mutations)\n4. **Database API** executes the validated query\n5. **AI formats** results into a clear natural language answer with insights\n6. **In-app message** delivers the answer immediately\n7. **Email report** with full technical details\n8. **Reject path**: Unsafe queries are blocked with an explanation\n\n**To activate:** Say "connect openai", configure your database API endpoint`,
  },

  // ===================================================================
  // 143. NEW CUSTOMER SUCCESS KICKOFF SEQUENCE (9 nodes)
  // ===================================================================

  "New Customer Success Kickoff Sequence": {
    nodes: [
      trigger("Webhook: New deal closed-won", "webhook_app", {
        source: "Salesforce / HubSpot CRM",
        event: "deal.closed_won",
        howItStarts:
          "A deal is marked as closed-won in the CRM, triggering the CS kickoff sequence",
        setup:
          "Connect your CRM webhook to fire when a deal stage changes to closed-won. POST the deal payload to your workflow URL.",
        inputFields: [],
      }),

      // 2. AI extract deal + customer details
      aiNode(
        "2",
        "AI extract deal details",
        280,
        "Read this CRM closed-won deal payload. Extract customer name, customer email, company name, deal value, product purchased, number of licenses/seats, CSM assigned (if any), sales rep name, and contract start date. Respond JSON: {customer_name: string, customer_email: string, company_name: string, deal_value: string, product_name: string, seat_count: string, csm_name: string, sales_rep: string, start_date: string}",
        "{webhook_payload}",
        [
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          { field: "deal_value", as: "deal_value", type: "string" },
          { field: "product_name", as: "product_name", type: "string" },
          { field: "seat_count", as: "seat_count", type: "string" },
          { field: "csm_name", as: "csm_name", type: "string" },
          { field: "sales_rep", as: "sales_rep", type: "string" },
          { field: "start_date", as: "start_date", type: "string" },
        ],
      ),

      // 3. AI generate personalized onboarding plan
      aiNode(
        "3",
        "AI: Generate onboarding plan",
        520,
        "You are a customer success manager. Create a 30-day onboarding plan based on the product, deal size, and seat count. Include milestone dates, key meetings, and success metrics. Respond JSON: {week_1_plan: string, week_2_plan: string, week_3_plan: string, week_4_plan: string, success_metrics: string, first_meeting_agenda: string}",
        "Customer: {customer_name} at {company_name}\\nProduct: {product_name}\\nDeal value: {deal_value}\\nSeats: {seat_count}\\nStart: {start_date}\\nSales rep: {sales_rep}",
        [
          { field: "week_1_plan", as: "week_1_plan", type: "string" },
          { field: "week_2_plan", as: "week_2_plan", type: "string" },
          { field: "week_3_plan", as: "week_3_plan", type: "string" },
          { field: "week_4_plan", as: "week_4_plan", type: "string" },
          { field: "success_metrics", as: "success_metrics", type: "string" },
          {
            field: "first_meeting_agenda",
            as: "first_meeting_agenda",
            type: "string",
          },
        ],
      ),

      // 4. Welcome email to customer
      emailNode(
        "4",
        "Welcome email to new customer",
        760,
        "{customer_email}",
        "Welcome to {product_name}, {customer_name}!",
        "<p>Hi {customer_name},</p><p>Welcome aboard! We're thrilled to have <strong>{company_name}</strong> as a new customer.</p><p>Your customer success manager is <strong>{csm_name}</strong>, who will be your primary point of contact.</p><p><strong>Here's what to expect in your first week:</strong></p><p>{week_1_plan}</p><p><strong>First meeting agenda:</strong></p><p>{first_meeting_agenda}</p><p>We'll reach out within 24 hours to schedule your kickoff call. In the meantime, you can start exploring at <a href='https://app.example.com/get-started'>app.example.com/get-started</a>.</p>",
      ),

      // 5. Create kickoff task in project management
      n("5", "hitApi", "Create kickoff task in Asana", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://app.asana.com/api/1.0/tasks",
          headers: [
            { key: "Authorization", value: "Bearer {asana_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "data",
              value:
                '{"name":"CS Kickoff: {company_name} - {product_name}","notes":"Deal value: {deal_value}\\nSeats: {seat_count}\\nSales rep: {sales_rep}\\n\\nWeek 1: {week_1_plan}\\nWeek 2: {week_2_plan}\\nWeek 3: {week_3_plan}\\nWeek 4: {week_4_plan}\\n\\nSuccess metrics: {success_metrics}","projects":["{asana_cs_project_id}"],"assignee":"me"}',
            },
          ],
          variables: [
            { name: "data.gid", customName: "task_id", type: "string" },
            {
              name: "data.permalink_url",
              customName: "task_url",
              type: "string",
            },
          ],
        },
      }),

      // 6. Slack: Alert CS team
      slackNode(
        "6",
        "Notify #customer-success: New customer",
        1240,
        "New Customer Kickoff!\\n\\nCompany: {company_name}\\nContact: {customer_name} ({customer_email})\\nProduct: {product_name} | Value: {deal_value} | Seats: {seat_count}\\nCSM: {csm_name} | Sales: {sales_rep}\\nStart date: {start_date}\\n\\nAsana task: {task_url}\\nOnboarding plan generated with 4-week milestones.",
      ),

      // 7. Delay 3 days
      n("7", "delayAction", "Wait 3 days", 1480, {
        delayAction: { time: 3, timeFormat: "days" },
      }),

      // 8. Check-in email
      emailNode(
        "8",
        "Check-in email: Day 3",
        1720,
        "{customer_email}",
        "How's your first few days with {product_name}, {customer_name}?",
        "<p>Hi {customer_name},</p><p>It's been a few days since you started with <strong>{product_name}</strong>. How's everything going?</p><p><strong>Week 2 plan:</strong></p><p>{week_2_plan}</p><p>If you have any questions or need help getting set up, reply directly to this email or reach out to {csm_name}.</p><p>We want to make sure you're on track to hit your success milestones!</p>",
      ),

      // 9. Update CRM: Onboarding started
      n("9", "hitApi", "Update CRM: Onboarding started", 1960, {
        hitApiDetails: {
          method: "PATCH",
          url: "https://api.hubapi.com/crm/v3/objects/companies/{company_name}",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "properties",
              value:
                '{"onboarding_status":"in_progress","csm_assigned":"{csm_name}","onboarding_plan":"4_week","kickoff_task":"{task_url}"}',
            },
          ],
          variables: [
            { name: "id", customName: "crm_company_id", type: "string" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
    summary: `**New Customer Success Kickoff Sequence** — Automates the entire CS onboarding process from deal close to Day 3 check-in.\n\n1. Webhook fires when a deal closes as won\n2. **AI extracts** customer, deal, and product details\n3. **AI generates** a personalized 30-day onboarding plan with milestones\n4. **Welcome email** with week 1 plan and first meeting agenda\n5. **Asana task** creates a tracked kickoff project for the CSM\n6. **Slack alert** notifies the CS team with full context\n7. **Waits 3 days** for initial engagement\n8. **Check-in email** with week 2 milestones\n9. **CRM update** records onboarding status\n\n**To activate:** Say "connect openai", "connect asana", "connect hubspot", "connect slack"`,
  },

  // ===================================================================
  // 144. NO EMAIL OPEN SEND PUSH OR IN-APP FALLBACK (6 nodes)
  // ===================================================================

  "No Email Open Send Push or In App Fallback": {
    nodes: [
      trigger("Webhook: Email sent event", "webhook_app", {
        source: "SendGrid / Postmark / Mailgun",
        event: "email.delivered",
        howItStarts:
          "An email is delivered to a contact; the workflow waits to check if it was opened, then falls back to push or in-app if not",
        setup:
          "Connect your email service provider webhook to fire on email delivery events. The workflow monitors for open events and triggers fallback channels if the email goes unopened.",
        inputFields: [],
      }),

      // 2. AI extract email event details
      aiNode(
        "2",
        "AI extract email delivery details",
        280,
        "Read this email delivery webhook payload. Extract recipient name, recipient email, email subject, message ID, and recipient's user ID or device token if available. Respond JSON: {recipient_name: string, recipient_email: string, email_subject: string, message_id: string, user_id: string}",
        "{webhook_payload}",
        [
          { field: "recipient_name", as: "recipient_name", type: "string" },
          { field: "recipient_email", as: "recipient_email", type: "string" },
          { field: "email_subject", as: "email_subject", type: "string" },
          { field: "message_id", as: "message_id", type: "string" },
          { field: "user_id", as: "user_id", type: "string" },
        ],
      ),

      // 3. Wait for open window
      n("3", "delayAction", "Wait 24 hours for email open", 520, {
        delayAction: { time: 24, timeFormat: "hours" },
      }),

      // 4. Check email open status via ESP API
      n("4", "hitApi", "Check email open status", 760, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.sendgrid.com/v3/messages/{message_id}",
          headers: [{ key: "Authorization", value: "Bearer {sendgrid_key}" }],
          variables: [
            { name: "status", customName: "email_status", type: "string" },
            {
              name: "events[0].event_name",
              customName: "last_event",
              type: "string",
            },
            { name: "opened", customName: "was_opened", type: "string" },
          ],
        },
      }),

      // 5. Condition: Was email opened?
      n("5", "condition", "Was email opened?", 1000, {
        conditionData: [
          {
            variable: "was_opened",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Push + in-app fallback (False path — not opened)
      n("6", "pushNotification", "Push fallback: Re-engage", 1240, {
        pushNotification: {
          title: "You missed this: {email_subject}",
          body: "Hi {recipient_name}, we sent you an important update. Tap to view the details you might have missed.",
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_2"), // False: not opened — send push
    ],
    summary: `**No Email Open — Send Push or In-App Fallback** — Monitors email delivery and automatically falls back to push notification if the email goes unopened.\n\n1. Webhook fires when an email is delivered\n2. **AI extracts** recipient details, subject, and message ID\n3. **Waits 24 hours** for the recipient to open the email\n4. **ESP API** checks the email open status\n5. **Condition** checks if the email was opened\n6. If NOT opened, sends a **push notification** as a fallback channel\n\n**To activate:** Say "connect openai", "connect sendgrid", "connect firebase"`,
  },

  // ===================================================================
  // 145. NO PUSH OPEN SEND EMAIL FALLBACK (6 nodes)
  // ===================================================================

  "No Push Open Send Email Fallback": {
    nodes: [
      trigger("Webhook: Push notification sent", "webhook_app", {
        source: "Firebase / OneSignal",
        event: "push.delivered",
        howItStarts:
          "A push notification is delivered to a user; the workflow waits to check if it was opened, then falls back to email if not",
        setup:
          "Connect your push notification provider (Firebase or OneSignal) to send delivery callbacks to your workflow URL. The workflow monitors for tap/open events and sends an email fallback.",
        inputFields: [],
      }),

      // 2. AI extract push event details
      aiNode(
        "2",
        "AI extract push delivery details",
        280,
        "Read this push notification delivery webhook payload. Extract recipient name, recipient email, notification title, notification body, notification ID, and device type. Respond JSON: {recipient_name: string, recipient_email: string, push_title: string, push_body: string, notification_id: string, device_type: string}",
        "{webhook_payload}",
        [
          { field: "recipient_name", as: "recipient_name", type: "string" },
          { field: "recipient_email", as: "recipient_email", type: "string" },
          { field: "push_title", as: "push_title", type: "string" },
          { field: "push_body", as: "push_body", type: "string" },
          { field: "notification_id", as: "notification_id", type: "string" },
          { field: "device_type", as: "device_type", type: "string" },
        ],
      ),

      // 3. Wait for push open window
      n("3", "delayAction", "Wait 4 hours for push open", 520, {
        delayAction: { time: 4, timeFormat: "hours" },
      }),

      // 4. Check push open status via API
      n("4", "hitApi", "Check push notification open status", 760, {
        hitApiDetails: {
          method: "GET",
          url: "https://onesignal.com/api/v1/notifications/{notification_id}?app_id={onesignal_app_id}",
          headers: [
            { key: "Authorization", value: "Basic {onesignal_rest_key}" },
          ],
          variables: [
            { name: "opened", customName: "push_opened_count", type: "number" },
            { name: "converted", customName: "push_converted", type: "number" },
            {
              name: "successful",
              customName: "push_delivered",
              type: "number",
            },
          ],
        },
      }),

      // 5. Condition: Was push opened?
      n("5", "condition", "Was push notification opened?", 1000, {
        conditionData: [
          {
            variable: "push_opened_count",
            value: "0",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Email fallback (False path — push not opened)
      emailNode(
        "6",
        "Email fallback: Resend as email",
        1240,
        "{recipient_email}",
        "{push_title}",
        "<p>Hi {recipient_name},</p><p>We sent you a notification on your {device_type} that you may have missed:</p><blockquote><strong>{push_title}</strong><br/>{push_body}</blockquote><p>We wanted to make sure you didn't miss this important update. Tap below to take action:</p><p><a href='https://app.example.com/notifications/{notification_id}'>View Details</a></p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_2"), // False: push not opened — send email
    ],
    summary: `**No Push Open — Send Email Fallback** — Monitors push notification delivery and automatically falls back to email if the push goes unopened.\n\n1. Webhook fires when a push notification is delivered\n2. **AI extracts** recipient details, notification content, and device type\n3. **Waits 4 hours** for the user to tap/open the push\n4. **OneSignal API** checks the push open status\n5. **Condition** checks if the push was opened\n6. If NOT opened, sends an **email fallback** with the same content\n\n**To activate:** Say "connect openai", "connect onesignal"`,
  },

  // ===================================================================
  // 146. ONBOARDING COMPLETION CELEBRATION (7 nodes)
  // ===================================================================

  "Onboarding Completion Celebration": {
    nodes: [
      trigger("Webhook: Onboarding completed", "webhook_app", {
        source: "App Backend / Customer Success Platform",
        event: "onboarding.completed",
        howItStarts:
          "A user completes all onboarding steps, triggering a celebration and next-steps sequence",
        setup:
          "Connect your app backend to fire a webhook when all onboarding milestones are marked complete. Include user details and completion metrics in the payload.",
        inputFields: [],
      }),

      // 2. AI extract onboarding completion details
      aiNode(
        "2",
        "AI extract completion details",
        280,
        "Read this onboarding completion webhook payload. Extract user name, user email, company name, days to complete onboarding, total steps completed, features activated, and account plan. Respond JSON: {user_name: string, user_email: string, company_name: string, days_to_complete: string, steps_completed: string, features_activated: string, account_plan: string}",
        "{webhook_payload}",
        [
          { field: "user_name", as: "user_name", type: "string" },
          { field: "user_email", as: "user_email", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          { field: "days_to_complete", as: "days_to_complete", type: "string" },
          { field: "steps_completed", as: "steps_completed", type: "string" },
          {
            field: "features_activated",
            as: "features_activated",
            type: "string",
          },
          { field: "account_plan", as: "account_plan", type: "string" },
        ],
      ),

      // 3. AI generate personalized next steps
      aiNode(
        "3",
        "AI: Personalized next steps",
        520,
        "You are a customer success advisor. Based on the user's completed onboarding and activated features, suggest 3 specific next actions that will help them get maximum value. Be specific to their plan and features. Respond JSON: {next_step_1: string, next_step_2: string, next_step_3: string, power_tip: string, estimated_time_to_value: string}",
        "User: {user_name} at {company_name}\\nPlan: {account_plan}\\nCompleted in {days_to_complete} days\\nSteps: {steps_completed}\\nFeatures: {features_activated}",
        [
          { field: "next_step_1", as: "next_step_1", type: "string" },
          { field: "next_step_2", as: "next_step_2", type: "string" },
          { field: "next_step_3", as: "next_step_3", type: "string" },
          { field: "power_tip", as: "power_tip", type: "string" },
          {
            field: "estimated_time_to_value",
            as: "time_to_value",
            type: "string",
          },
        ],
      ),

      // 4. Celebration email
      emailNode(
        "4",
        "Celebration email: Onboarding complete!",
        760,
        "{user_email}",
        "Congratulations, {user_name}! You've completed onboarding!",
        "<p>Hi {user_name},</p><p>Amazing work! You've completed all <strong>{steps_completed}</strong> onboarding steps in just <strong>{days_to_complete} days</strong>.</p><p>You've activated: <strong>{features_activated}</strong></p><h3>Your Next Steps</h3><ol><li>{next_step_1}</li><li>{next_step_2}</li><li>{next_step_3}</li></ol><p><strong>Power tip:</strong> {power_tip}</p><p>Estimated time to full value: {time_to_value}</p><p>Keep up the great momentum!</p>",
      ),

      // 5. Push notification celebration
      n("5", "pushNotification", "Push: Onboarding achievement", 1000, {
        pushNotification: {
          title: "You did it, {user_name}!",
          body: "Onboarding complete! You finished all {steps_completed} steps in {days_to_complete} days. Check out your personalized next steps.",
        },
      }),

      // 6. In-app celebration message
      n("6", "sendMessage", "In-app: Achievement unlocked", 1240, {
        data: {
          title:
            "Achievement Unlocked! You've completed onboarding in {days_to_complete} days with {features_activated} features activated. Here's your power tip: {power_tip}",
        },
      }),

      // 7. Notify CS team
      slackNode(
        "7",
        "Notify #customer-success: Onboarding win",
        1480,
        "Onboarding Complete!\\nUser: {user_name} ({user_email})\\nCompany: {company_name} | Plan: {account_plan}\\nCompleted in {days_to_complete} days | Steps: {steps_completed}\\nFeatures: {features_activated}\\nNext steps delivered. Time to value: {time_to_value}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
    summary: `**Onboarding Completion Celebration** — Celebrates users who complete onboarding with personalized next steps across email, push, and in-app channels.\n\n1. Webhook fires when all onboarding milestones are complete\n2. **AI extracts** completion metrics: days, steps, features activated\n3. **AI generates** 3 personalized next steps and a power tip\n4. **Celebration email** with achievement summary and next actions\n5. **Push notification** celebrating the achievement\n6. **In-app message** with achievement badge and power tip\n7. **Slack notification** to the CS team with full onboarding metrics\n\n**To activate:** Say "connect openai", "connect slack", "connect firebase"`,
  },

  // ===================================================================
  // 147. ONBOARDING FEEDBACK SURVEY (7 nodes)
  // ===================================================================

  "Onboarding Feedback Survey": {
    nodes: [
      trigger("Webhook: Onboarding milestone reached", "webhook_app", {
        source: "App Backend",
        event: "onboarding.milestone",
        howItStarts:
          "A user completes a key onboarding milestone, triggering a feedback survey to capture their experience",
        setup:
          "Connect your app backend to fire a webhook when a user reaches key onboarding milestones (e.g., first feature used, first integration, first week). POST the milestone data to your workflow URL.",
        inputFields: [],
      }),

      // 2. AI extract milestone details
      aiNode(
        "2",
        "AI extract milestone details",
        280,
        "Read this onboarding milestone webhook payload. Extract user name, user email, milestone name, milestone number, total milestones, days since signup, and features used so far. Respond JSON: {user_name: string, user_email: string, milestone_name: string, milestone_number: string, total_milestones: string, days_since_signup: string, features_used: string}",
        "{webhook_payload}",
        [
          { field: "user_name", as: "user_name", type: "string" },
          { field: "user_email", as: "user_email", type: "string" },
          { field: "milestone_name", as: "milestone_name", type: "string" },
          { field: "milestone_number", as: "milestone_number", type: "string" },
          { field: "total_milestones", as: "total_milestones", type: "string" },
          {
            field: "days_since_signup",
            as: "days_since_signup",
            type: "string",
          },
          { field: "features_used", as: "features_used", type: "string" },
        ],
      ),

      // 3. Generate contextual survey via AI
      aiNode(
        "3",
        "AI: Generate contextual survey questions",
        520,
        "You are a UX researcher. Based on the user's onboarding milestone and features used, generate 3 targeted feedback questions. One should be a rating (1-10), one open-ended about experience, and one about what they'd improve. Keep questions brief and specific to their milestone. Respond JSON: {rating_question: string, experience_question: string, improvement_question: string, survey_intro: string}",
        "User: {user_name}\\nMilestone: {milestone_name} ({milestone_number}/{total_milestones})\\nDays since signup: {days_since_signup}\\nFeatures used: {features_used}",
        [
          { field: "rating_question", as: "rating_question", type: "string" },
          {
            field: "experience_question",
            as: "experience_question",
            type: "string",
          },
          {
            field: "improvement_question",
            as: "improvement_question",
            type: "string",
          },
          { field: "survey_intro", as: "survey_intro", type: "string" },
        ],
      ),

      // 4. Send survey email
      emailNode(
        "4",
        "Email: Onboarding feedback survey",
        760,
        "{user_email}",
        "Quick feedback on {milestone_name}, {user_name}?",
        "<p>Hi {user_name},</p><p>{survey_intro}</p><p>You've reached <strong>{milestone_name}</strong> — milestone {milestone_number} of {total_milestones}. We'd love your quick feedback (takes 30 seconds):</p><ol><li><strong>{rating_question}</strong></li><li><strong>{experience_question}</strong></li><li><strong>{improvement_question}</strong></li></ol><p><a href='https://app.example.com/feedback?milestone={milestone_number}&user={user_email}'>Share Your Feedback</a></p><p>Your input directly shapes how we improve the product. Thank you!</p>",
      ),

      // 5. In-app survey prompt
      n("5", "sendMessage", "In-app: Survey prompt", 1000, {
        data: {
          title:
            "{survey_intro} You just reached {milestone_name}! Quick question: {rating_question} Tap here to share your feedback and help us improve.",
        },
      }),

      // 6. Delay 2 days then check
      n("6", "delayAction", "Wait 2 days for response", 1240, {
        delayAction: { time: 2, timeFormat: "days" },
      }),

      // 7. WhatsApp follow-up reminder
      n("7", "whatsApp", "WhatsApp: Survey reminder", 1480, {
        whatsappDetails: {
          templateName: "onboarding_feedback",
          lang: "en",
          to: "{user_email}",
          smsBody:
            "Hi {user_name}, we'd love to hear how your onboarding is going! You've reached {milestone_name} in {days_since_signup} days. Quick feedback: {rating_question} Reply here or tap the link in your email. Your input matters!",
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
    summary: `**Onboarding Feedback Survey** — Sends contextual feedback surveys at key onboarding milestones across email, in-app, and WhatsApp.\n\n1. Webhook fires when a user reaches a key onboarding milestone\n2. **AI extracts** milestone details and features used\n3. **AI generates** 3 targeted survey questions specific to the milestone\n4. **Email survey** with a direct feedback link\n5. **In-app prompt** with the key rating question\n6. **Waits 2 days** for the user to respond\n7. **WhatsApp follow-up** if the survey is not yet completed\n\n**To activate:** Say "connect openai", "connect whatsapp"`,
  },
};
